import React from "react";
import { Helmet } from "react-helmet-async";
import { SITE_NAME, SITE_URL, absoluteUrl } from "../data/site";

const DEFAULT_IMAGE = SITE_URL + "/og.png";

/**
 * One place that owns <head> for every route: title, description, canonical,
 * Open Graph, Twitter card and any JSON-LD graph the page wants to emit.
 *
 * `path` is the route path ("/faq"); the canonical is always built from
 * SITE_URL so a page can never canonicalise itself to the wrong hostname.
 */
const Seo = ({
  title,
  description,
  path = "/",
  keywords,
  image = DEFAULT_IMAGE,
  schema,
  noindex = false,
  publishedTime,
  modifiedTime,
}) => {
  const canonical = absoluteUrl(path);
  const schemaList = Array.isArray(schema) ? schema : schema ? [schema] : [];

  return (
    <Helmet prioritizeSeoTags>
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={canonical} />
      <meta
        name="robots"
        content={
          noindex
            ? "noindex,follow"
            : "index,follow,max-snippet:-1,max-image-preview:large,max-video-preview:-1"
        }
      />
      <meta name="author" content={SITE_NAME} />
      <meta name="publisher" content={SITE_NAME} />
      <meta name="geo.region" content="IN-RJ" />
      <meta name="geo.placename" content="Rajasthan, India" />
      <meta name="language" content="Hindi, English" />
      <meta name="distribution" content="global" />
      <meta name="rating" content="general" />

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_IN" />
      <meta property="og:locale:alternate" content="hi_IN" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={title} />
      {modifiedTime && (
        <meta property="og:updated_time" content={modifiedTime} />
      )}
      {publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {modifiedTime && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:url" content={canonical} />

      {schemaList.map((entry, idx) => (
        <script type="application/ld+json" key={idx}>
          {JSON.stringify(entry)}
        </script>
      ))}
    </Helmet>
  );
};

export default Seo;
